import { ReducerCreators, type PayloadAction } from '@reduxjs/toolkit';
import { FetchError } from '@/connection/helpers';
import toastTrigger from '@ui/toast/toast-trigger';
import type { WatermarksState } from './types';
import { initialState } from './initial';
import UpdateWatermark from '@/connection/watermarks/update-watermark';
import CreateWatermark from '@/connection/watermarks/create-watermark';
import DeleteWatermark from '@/connection/watermarks/delete-watermark';
import { IWatermarkElement, IWatermarkSet } from '@/types/watermarks.types';
import GetWatermarksByChannel from '@/connection/watermarks/get-watermarks-by-channel';

const handleRejected = (state: WatermarksState, message: string, error: unknown): void => {
  state.error = true;
  state.loading = false;
  console.error(error);
  toastTrigger(message, {
    type: 'error',
    autoClose: 10000,
  });
};

export const reducers = (create: ReducerCreators<WatermarksState>) => ({
  loadWatermarkSets: create.asyncThunk(
    async ({ channel }: { channel: number }, { rejectWithValue }) => {
      try {
        return await GetWatermarksByChannel({ channel });
      } catch (e) {
        return rejectWithValue(e instanceof FetchError ? e.body : e);
      }
    },
    {
      pending: (state) => {
        state.loading = true;
      },
      fulfilled: (state, action) => {
        state.watermarkSets = action.payload;
        state.loading = false;
        state.error = false;
      },
      rejected: (state, action) => handleRejected(state, 'Error loading watermarks.', action.error),
    }
  ),

  updateWatermarkSet: create.asyncThunk(
    async (watermark: IWatermarkSet, { rejectWithValue }) => {
      try {
        return await UpdateWatermark(watermark);
      } catch (e) {
        return rejectWithValue(e instanceof FetchError ? e.body : e);
      }
    },
    {
      pending: (state) => {
        state.loading = true;
      },
      fulfilled: (state, action) => {
        const updated = action.payload;
        state.watermarkSets = state.watermarkSets?.map((ws) => (ws.uuid === updated.uuid ? updated : ws)) ?? null;

        if (state.currentWatermarkSet?.uuid === updated.uuid) {
          state.currentWatermarkSet = updated;
        }
        state.loading = false;
        state.error = false;
      },
      rejected: (state, action) => handleRejected(state, 'Error updating watermark.', action.error),
    }
  ),

  createWatermarkSet: create.asyncThunk(
    async (watermark: { channel: number; name: string; elements: IWatermarkElement[] }, { rejectWithValue }) => {
      try {
        return await CreateWatermark(watermark);
      } catch (e) {
        return rejectWithValue(e instanceof FetchError ? e.body : e);
      }
    },
    {
      pending: (state) => {
        state.loading = true;
      },
      fulfilled: (state, action) => {
        state.watermarkSets = [...(state.watermarkSets ?? []), action.payload];
        state.currentWatermarkSet = action.payload;
        state.loading = false;
        state.error = false;
      },
      rejected: (state, action) => handleRejected(state, 'Error creating watermark.', action.error),
    }
  ),

  deleteWatermarkSet: create.asyncThunk(
    async ({ channel, uuid }: { channel: number; uuid: string }, { rejectWithValue }) => {
      try {
        await DeleteWatermark({ channel, uuid });
        return uuid;
      } catch (e) {
        return rejectWithValue(e instanceof FetchError ? e.body : e);
      }
    },
    {
      pending: (state) => {
        state.loading = true;
      },
      fulfilled: (state, action) => {
        state.watermarkSets = state.watermarkSets?.filter((ws) => ws.uuid !== action.payload) ?? null;
        if (state.currentWatermarkSet?.uuid === action.payload) {
          state.currentWatermarkSet = null;
        }
        state.loading = false;
        state.error = false;
      },
      rejected: (state, action) => handleRejected(state, 'Error deleting watermark.', action.error),
    }
  ),

  setCurrentWatermarkSet: create.reducer((state, action: PayloadAction<IWatermarkSet>) => {
    state.currentWatermarkSet = action.payload;
  }),

  clearCurrentWatermarkSet: create.reducer((state) => {
    state.currentWatermarkSet = null;
  }),

  resetWatermarksReducer: create.reducer(() => initialState),
});
